/**
 * Mid Call Drop — server-side liveness heartbeat.
 *
 * While a call is connected, tell the Realtime server "this tab is alive on contactId".
 * If the tab never comes back (permanent offline, tab closed, crash, sleep), heartbeats
 * stop and the server watchdog ends the silent interpreter leg via barge-bot.
 * The client Streams path (ccpNetworkDropStorage) still owns recover / reload.
 *
 * Channels (any one keeps the call alive server-side):
 *  - InterpreterHub `CallHeartbeat` every 5s
 *  - HTTP POST /api/realtime/presence/call-liveness when the hub is not connected (hub retries give up)
 *  - reply to server `call:liveness` pings (WebSocket handler — not subject to timer throttling)
 *
 * After a mid-call reload the page beats for the pending drop contact (ccp_network_drop
 * timedOut+fromReload) from JS boot — before the hub or Streams are back — so the server's
 * fast "socket closed" path never races the client's own reload end.
 */
import * as signalR from '@microsoft/signalr';
import { streamsStore } from './streamsStore';
import { getInterpreterHubConnection } from './agentNetworkTelemetry/interpreterHubConnection';
import { parseCallHeaderCode } from './incomingCallHandledState';
import { getAuth } from '../app/modules/auth/core/AuthHelpers';
import { readNetworkDropState } from './ccpNetworkDropStorage';

const HEARTBEAT_MS = 5_000;
// Ignore a leftover reload marker from an old session.
const RELOAD_MARKER_MAX_AGE_MS = 120_000;

interface LiveCall {
    contactId: string;
    callHeaderCode: number | null;
    /** 'reload' = from the ccp_network_drop marker, not a call Streams reports connected. */
    source?: 'connected' | 'reload';
}

let current: LiveCall | null = null;
let started = false;
let isMasterTab = false;

/**
 * Fed by the Connect tab election. ccp_network_drop is shared localStorage, so only the
 * master tab (the one that runs Streams and will end the call) may beat for the reload marker.
 */
export function setCallLivenessMasterTab(master: boolean): void {
    isMasterTab = master;
}

function readLiveCall(): LiveCall | null {
    const s = streamsStore.getSnapshot();
    if (s.callPhase === 'connected' && s.contactId) {
        return {
            contactId: s.contactId,
            callHeaderCode: parseCallHeaderCode(s.activeCallData?.callHeaderId),
            source: 'connected',
        };
    }
    // Reloaded mid-call: the client reload path is about to end this leg itself.
    if (!isMasterTab) return null;
    const drop = readNetworkDropState();
    if (
        drop.phase === 'timedOut' &&
        drop.fromReload &&
        drop.contactId &&
        drop.startedAt != null &&
        Date.now() - drop.startedAt < RELOAD_MARKER_MAX_AGE_MS
    ) {
        return {
            contactId: drop.contactId,
            callHeaderCode: parseCallHeaderCode(drop.callHeaderId),
            source: 'reload',
        };
    }
    return null;
}

async function postHttp(body: LiveCall & { ended?: boolean }): Promise<void> {
    const baseUrl = process.env.REACT_APP_BASE_URL || '';
    const token = getAuth()?.token;
    if (!baseUrl || !token) return;
    try {
        await fetch(`${baseUrl}/api/realtime/presence/call-liveness`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify(body),
            keepalive: true,
        });
    } catch {
        /* offline — the server watchdog is meant to notice */
    }
}

function sendBeat(call: LiveCall): void {
    const hub = getInterpreterHubConnection();
    if (hub?.state === signalR.HubConnectionState.Connected) {
        hub.invoke('CallHeartbeat', call).catch(() => void postHttp(call));
        return;
    }
    void postHttp(call);
}

function sendEnded(call: LiveCall): void {
    const hub = getInterpreterHubConnection();
    if (hub?.state === signalR.HubConnectionState.Connected) {
        hub.invoke('CallEnded', call.contactId).catch(() => void postHttp({ ...call, ended: true }));
        return;
    }
    void postHttp({ ...call, ended: true });
}

function reconcile(): void {
    const next = readLiveCall();
    if (next?.contactId === current?.contactId) {
        // Same call — push immediately once the call header code becomes known.
        if (next && current && next.callHeaderCode !== current.callHeaderCode) {
            current = next;
            sendBeat(current);
        } else if (next) {
            current = next;
        }
        return;
    }

    // Only a real store transition reaches here; page unload never does, so a closed /
    // reloading tab never sends CallEnded — that silence is exactly what we detect.
    // A reload-marker call just goes quiet: if the reloaded page never found the contact,
    // the server must still verify the leg with Connect rather than stop watching it.
    if (current && current.source !== 'reload') sendEnded(current);
    current = next;
    if (current) sendBeat(current);
}

/** Beat now (server ping, hub reconnected). No-op when not on a connected call. */
export function sendCallLivenessBeatNow(): void {
    if (current) sendBeat(current);
}

/** Idempotent. Call once the interpreter hub connection exists. */
export function startCallLivenessHeartbeat(): void {
    if (started) return;
    started = true;
    streamsStore.subscribe(reconcile);
    reconcile();
    // Network back: beat right away so a sub-25s blip (which the client keeps) never looks
    // longer than it was to the server. Retry once — the first request can race the network.
    window.addEventListener('online', () => {
        reconcile();
        sendCallLivenessBeatNow();
        setTimeout(sendCallLivenessBeatNow, 2_000);
    });
    // Also re-reads state each tick (the reload marker is cleared without a store update).
    setInterval(() => {
        reconcile();
        if (current) sendBeat(current);
    }, HEARTBEAT_MS);
}
